// Extracted verbatim from the v3 monolith (see BIRDSEYE.md module split plan).

import { DATA_PAGE, DATA_FACET_COUNT } from './constants.js';
import { toArray, toInt, normalizeText, debounce } from './utils.js';
import { isNativeFormControl, isChipActive, setChipActive, isChipExclusive, isWildcardChip, getChipGroup, isControlDisabled, scheduleNativeToggleUiSync, toggleNativeBinaryControl } from './controls.js';

export var _deactivateChipSiblings = function (control) {
  var field = normalizeText(control.getAttribute('data-pf-filter') || '');
  var group = getChipGroup(control);

  this.filterControls.forEach(function (candidate) {
    if (candidate === control || isNativeFormControl(candidate)) {
      return;
    }

    var candidateField = normalizeText(candidate.getAttribute('data-pf-filter') || '');
    if (candidateField !== field) {
      return;
    }

    if (getChipGroup(candidate) !== group) {
      return;
    }

    setChipActive(candidate, false);
  });
};

export var _toggleChipControl = function (control) {
  if (isControlDisabled(control)) {
    return;
  }

  // A wildcard chip ("All") clears its field rather than toggling: clicking it drops
  // every other value, and clicking it again must not switch it off and leave the row
  // with nothing lit while the list still shows everything.
  if (isWildcardChip(control)) {
    this._deactivateChipSiblings(control);
    setChipActive(control, true);
    return;
  }

  var nextState = !isChipActive(control);
  if (nextState && isChipExclusive(control)) {
    this._deactivateChipSiblings(control);
  }

  setChipActive(control, nextState);
};

// A wildcard chip is lit exactly when its field carries no other active value, so "All"
// tracks the real state no matter how it got there — a sibling click, a toggle back off,
// reset(), or a URL restore. Without this the field's selection and the row's highlight
// drift apart.
export var _syncWildcardChips = function () {
  var claimed = Object.create(null);

  var keyOf = function (control) {
    var field = normalizeText(control.getAttribute('data-pf-filter') || '');
    return field ? field + '::' + getChipGroup(control) : '';
  };

  this.filterControls.forEach(function (control) {
    if (isNativeFormControl(control) || isWildcardChip(control) || !isChipActive(control)) {
      return;
    }

    var key = keyOf(control);
    if (key) {
      claimed[key] = true;
    }
  });

  this.filterControls.forEach(function (control) {
    if (isNativeFormControl(control) || !isWildcardChip(control)) {
      return;
    }

    var key = keyOf(control);
    if (key) {
      setChipActive(control, !claimed[key]);
    }
  });
};

export var _bindControlEvents = function () {
  var self = this;

  var onFilterChange = function () {
    self.applyFilters({ resetPage: true, reason: 'filter' });
  };

  this.filterControls.forEach(function (control) {
    if (!isNativeFormControl(control)) {
      if (!control.hasAttribute('role')) {
        control.setAttribute('role', 'button');
      }
      if (!control.hasAttribute('tabindex')) {
        control.setAttribute('tabindex', '0');
      }
      if (!control.hasAttribute('aria-pressed')) {
        control.setAttribute('aria-pressed', isChipActive(control) ? 'true' : 'false');
      }

      self._listen(control, 'click', function (event) {
        event.preventDefault();
        if (isControlDisabled(control)) {
          return;
        }
        self._toggleChipControl(control);
        onFilterChange();
      });

      self._listen(control, 'keydown', function (event) {
        if (event.key !== 'Enter' && event.key !== ' ') {
          return;
        }
        event.preventDefault();
        if (isControlDisabled(control)) {
          return;
        }
        self._toggleChipControl(control);
        onFilterChange();
      });
      return;
    }

    var tag = control.tagName.toLowerCase();
    var type = (control.type || '').toLowerCase();
    if (type === 'checkbox' || type === 'radio') {
      scheduleNativeToggleUiSync(control);
      control.__pfLastChecked = !!control.checked;
    }
    var isInputText = tag !== 'select' && type !== 'checkbox' && type !== 'radio';
    var eventName = isInputText ? 'input' : 'change';
    var listener;
    if (isInputText) {
      listener = debounce(onFilterChange, self.searchDebounce);
    } else if (type === 'checkbox' || type === 'radio') {
      listener = function () {
        control.__pfLastChecked = !!control.checked;
        scheduleNativeToggleUiSync(control);
        onFilterChange();
      };
    } else {
      listener = onFilterChange;
    }

    self._listen(control, eventName, listener);
    if (type === 'checkbox' || type === 'radio') {
      var nativeHost = control.closest('label, .w-checkbox, .w-radio, [data-pf-option]');
      var hostTag = nativeHost && nativeHost.tagName ? nativeHost.tagName.toLowerCase() : '';
      var hostIsNativeLabel = hostTag === 'label';
      var hostHasCustomVisual = !!(
        nativeHost &&
        nativeHost.querySelector('.w-checkbox-input--inputType-custom, .w-radio-input--inputType-custom')
      );
      self._listen(control, 'click', function () {
        window.setTimeout(function () {
          var nextChecked = !!control.checked;
          if (nextChecked === !!control.__pfLastChecked) {
            return;
          }

          control.__pfLastChecked = nextChecked;
          scheduleNativeToggleUiSync(control);
          onFilterChange();
        }, 0);
      });

      if (nativeHost) {
        self._listen(nativeHost, 'click', function (event) {
          if (isControlDisabled(control)) {
            return;
          }

          if (event.target && event.target.closest && event.target.closest(DATA_FACET_COUNT)) {
            return;
          }

          if (event.target === control) {
            return;
          }

          if (event.target && event.target.closest && event.target.closest('a,button,[data-pf-ignore-toggle="true"]')) {
            return;
          }

          if (hostHasCustomVisual && event.target !== control) {
            event.preventDefault();
            var toggled = toggleNativeBinaryControl(control, type, control.form || self.wrapper);
            if (!toggled) {
              return;
            }

            control.__pfLastChecked = !!control.checked;
            scheduleNativeToggleUiSync(control);
            control.dispatchEvent(new Event('input', { bubbles: true }));
            control.dispatchEvent(new Event('change', { bubbles: true }));
            return;
          }

          if (hostIsNativeLabel) {
            var beforeChecked = !!control.checked;
            window.setTimeout(function () {
              if (isControlDisabled(control)) {
                return;
              }

              var afterChecked = !!control.checked;
              if (afterChecked !== beforeChecked) {
                return;
              }

              if (typeof control.click === 'function') {
                control.click();
                return;
              }

              var fallbackToggled = toggleNativeBinaryControl(control, type, control.form || self.wrapper);
              if (!fallbackToggled) {
                return;
              }
              control.dispatchEvent(new Event('change', { bubbles: true }));
            }, 0);
            return;
          }

          if (typeof control.click === 'function') {
            control.click();
            return;
          }

          var toggled = toggleNativeBinaryControl(control, type, control.form || self.wrapper);
          if (!toggled) {
            return;
          }
          control.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }
    }
  });

  this.searchControls.forEach(function (control) {
    self._listen(
      control,
      'input',
      debounce(function () {
        self.applyFilters({ resetPage: true, reason: 'search' });
      }, self.searchDebounce)
    );
  });

  this.resetControls.forEach(function (control) {
    self._listen(control, 'click', function (event) {
      event.preventDefault();
      self.reset();
    });
  });

  this.logicToggles.forEach(function (toggle) {
    var evtName = (toggle.type || '').toLowerCase() === 'checkbox' ? 'change' : 'input';
    self._listen(toggle, evtName, function () {
      self.applyFilters({ resetPage: true, reason: 'logic' });
    });
  });

  this.pageButtons.prev.forEach(function (btn) {
    self._listen(btn, 'click', function (event) {
      event.preventDefault();
      if (isControlDisabled(btn)) {
        return;
      }
      self.goToPage(self.currentPage - 1);
    });
  });

  this.pageButtons.next.forEach(function (btn) {
    self._listen(btn, 'click', function (event) {
      event.preventDefault();
      if (isControlDisabled(btn)) {
        return;
      }
      self.goToPage(self.currentPage + 1);
    });
  });

  if (this.tagsContainer) {
    this._listen(this.tagsContainer, 'click', function (event) {
      self._handleTagClick(event);
    });
  }

  this._bindNumberContainerHandler();
};

export var _bindNumberContainerHandler = function () {
  var self = this;

  if (!this.pageButtons.numbersContainer) {
    return;
  }

  if (this._numberClickCleanup) {
    this._numberClickCleanup();
    this._numberClickCleanup = null;
  }

  this._numberClickHandler = function (event) {
    var target = event.target.closest(DATA_PAGE + '[data-pf-page="number"]');
    if (!target || !self.pageButtons.numbersContainer.contains(target)) {
      return;
    }

    event.preventDefault();
    if (isControlDisabled(target)) {
      return;
    }
    var page = toInt(target.getAttribute('data-pf-page-number'), NaN);
    if (!Number.isFinite(page)) {
      return;
    }

    self.goToPage(page);
  };

  this._numberClickCleanup = this._listen(this.pageButtons.numbersContainer, 'click', this._numberClickHandler);
};

export var _observeListMutations = function () {
  var self = this;

  if (!window.MutationObserver || !this.list) {
    return;
  }

  if (this._mutationObserver) {
    this._mutationObserver.disconnect();
  }

  this._mutationObserver = new MutationObserver(function (records) {
    if (self._mutationMuteCounter > 0) {
      return;
    }

    var changed = false;

    records.forEach(function (record) {
      toArray(record.addedNodes).forEach(function (addedNode) {
        self._collectItemNodes(addedNode).forEach(function (itemNode) {
          if (self._addItemNode(itemNode)) {
            changed = true;
          }
        });
      });

      toArray(record.removedNodes).forEach(function (removedNode) {
        self._collectItemNodes(removedNode).forEach(function (itemNode) {
          if (self.list.contains(itemNode)) {
            return;
          }
          if (self._removeItemNode(itemNode)) {
            changed = true;
          }
        });
      });
    });

    if (!changed) {
      return;
    }

    self.applyFilters({
      resetPage: false,
      reason: 'dom-change',
      skipAutoEnsure: true,
    });
  });

  this._mutationObserver.observe(this.list, {
    childList: true,
    subtree: true,
  });
};

export var _muteMutations = function () {
  this._mutationMuteCounter += 1;
};

export var _unmuteMutationsAsync = function () {
  var self = this;
  Promise.resolve().then(function () {
    self._mutationMuteCounter = Math.max(0, self._mutationMuteCounter - 1);
  });
};

